import { useState } from 'react';
import { X } from 'lucide-react';
import type { QuoteType, ResidentialItemType } from '../../types/admin';
import { CustomDropdown } from '../ui/CustomDropdown';

/**
 * Filter sheet shared by the master and org inventory pages.
 *
 * Mobile: bottom sheet. md+: centred dialog. Edits are local until Apply,
 * matching the Sales page's FilterSheet.
 */

export type CategoryFilter = ResidentialItemType | 'all';

export const CATEGORY_LABELS: Record<ResidentialItemType, string> = {
  customer_item: 'Customer Item',
  packing_item: 'Packing Item',
  equipment: 'Equipment',
};

export interface InventoryFilters {
  /** Commercial view only: item type or 'all'. */
  typeFilter: string;
  /** Both views: item category or 'all'. */
  categoryFilter: CategoryFilter;
  /** Residential view only: room id or 'all'. */
  roomFilter: string;
}

export const DEFAULT_INVENTORY_FILTERS: InventoryFilters = {
  typeFilter: 'all',
  categoryFilter: 'all',
  roomFilter: 'all',
};

/** Number of filters that are narrowing the current view. */
export function countActiveInventoryFilters(f: InventoryFilters, viewMode: QuoteType): number {
  let n = 0;
  if (viewMode === 'commercial' && f.typeFilter !== 'all') n++;
  if (f.categoryFilter !== 'all') n++;
  if (viewMode === 'residential' && f.roomFilter !== 'all') n++;
  return n;
}

interface InventoryFilterSheetProps {
  viewMode: QuoteType;
  filters: InventoryFilters;
  /** Commercial item types, e.g. Casegoods / Modular Component. */
  typeOptions: readonly string[];
  /** Residential rooms (id + name). */
  rooms: ReadonlyArray<{ id: string; name: string }>;
  onApply: (f: InventoryFilters) => void;
  onClose: () => void;
}

export function InventoryFilterSheet({ viewMode, filters, typeOptions, rooms, onApply, onClose }: InventoryFilterSheetProps) {
  const [typeFilter, setTypeFilter] = useState(filters.typeFilter);
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>(filters.categoryFilter);
  const [roomFilter, setRoomFilter] = useState(filters.roomFilter);
  const isCommercial = viewMode === 'commercial';

  function handleApply() {
    onApply({ typeFilter, categoryFilter, roomFilter });
    onClose();
  }

  function handleReset() {
    onApply(DEFAULT_INVENTORY_FILTERS);
    onClose();
  }

  const RadioIcon = ({ checked }: { checked: boolean }) => (
    <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0
      ${checked ? 'border-plum' : 'border-stone'}`}>
      {checked && <span className="w-2 h-2 rounded-full bg-plum" />}
    </span>
  );

  function OptionList<T extends string>({ value, options, onChange }: {
    value: T;
    options: ReadonlyArray<{ value: T; label: string }>;
    onChange: (v: T) => void;
  }) {
    return (
      <div className="flex flex-col gap-2 mb-5">
        {options.map(opt => (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            className={`flex items-center gap-3 min-h-11 px-3 rounded-xl border text-left
              transition-colors duration-150
              ${value === opt.value ? 'border-plum bg-plum/5' : 'border-stone bg-white hover:border-plum/40'}`}
          >
            <RadioIcon checked={value === opt.value} />
            <span className={`text-small font-medium ${value === opt.value ? 'text-plum' : 'text-midnight'}`}>
              {opt.label}
            </span>
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div
        className="relative bg-white rounded-t-2xl px-4 pt-5 pb-10 max-h-[80vh] overflow-y-auto
                   md:rounded-2xl md:max-w-sm md:w-full md:pb-6"
        role="dialog"
        aria-modal="true"
        aria-label="Filter items"
      >
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />

        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-h3 text-midnight">Filter Items</h2>
          <button
            type="button"
            onClick={onClose}
            className="w-11 h-11 -mr-2 flex items-center justify-center rounded-lg text-slate
                       hover:text-midnight hover:bg-mist transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {isCommercial && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Type</p>
            <OptionList
              value={typeFilter}
              onChange={setTypeFilter}
              options={[{ value: 'all', label: 'All Types' }, ...typeOptions.map(t => ({ value: t, label: t }))]}
            />
          </>
        )}

        <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Category</p>
        <OptionList<CategoryFilter>
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={[
            { value: 'all', label: 'All Categories' },
            { value: 'customer_item', label: CATEGORY_LABELS.customer_item },
            { value: 'packing_item', label: CATEGORY_LABELS.packing_item },
            { value: 'equipment', label: CATEGORY_LABELS.equipment },
          ]}
        />

        {!isCommercial && rooms.length > 0 && (
          <>
            <p className="text-[11px] font-bold uppercase tracking-widest text-slate mb-3">Room</p>
            <div className="mb-5">
              <CustomDropdown
                value={roomFilter}
                onChange={setRoomFilter}
                options={[
                  { value: 'all', label: 'All Rooms' },
                  ...rooms.map(r => ({ value: r.id, label: r.name })),
                ]}
              />
            </div>
          </>
        )}

        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleReset}
            className="flex-1 h-12 rounded-xl border border-stone text-small font-semibold text-slate
                       hover:border-plum/40 hover:text-midnight active:bg-mist
                       transition-colors duration-150"
          >
            Reset
          </button>
          <button
            type="button"
            onClick={handleApply}
            className="flex-1 h-12 rounded-xl bg-plum text-white text-[15px] font-semibold
                       hover:bg-rose active:opacity-80 transition-colors duration-150"
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Pills for the filters currently narrowing the view. Only non-"all" filters
 * render; each pill clears its own filter.
 */
export function ActiveInventoryFilterPills({ viewMode, filters, rooms, onChange }: {
  viewMode: QuoteType;
  filters: InventoryFilters;
  rooms: ReadonlyArray<{ id: string; name: string }>;
  onChange: (f: InventoryFilters) => void;
}) {
  const pills: Array<{ key: string; label: string; clear: () => void }> = [];
  if (viewMode === 'commercial' && filters.typeFilter !== 'all') {
    pills.push({ key: 'type', label: filters.typeFilter, clear: () => onChange({ ...filters, typeFilter: 'all' }) });
  }
  if (filters.categoryFilter !== 'all') {
    pills.push({ key: 'category', label: CATEGORY_LABELS[filters.categoryFilter], clear: () => onChange({ ...filters, categoryFilter: 'all' }) });
  }
  if (viewMode === 'residential' && filters.roomFilter !== 'all') {
    const room = rooms.find(r => r.id === filters.roomFilter);
    pills.push({ key: 'room', label: room?.name ?? 'Room', clear: () => onChange({ ...filters, roomFilter: 'all' }) });
  }
  if (pills.length === 0) return null;

  return (
    <div className="flex items-center gap-1.5 mb-3 flex-wrap">
      {pills.map(p => (
        <button
          key={p.key}
          type="button"
          onClick={p.clear}
          className="h-8 pl-3 pr-2 rounded-full bg-plum text-white text-[12px] font-semibold
                     flex items-center gap-1 hover:bg-rose transition-colors duration-150"
          aria-label={`Clear filter ${p.label}`}
        >
          {p.label}
          <X size={12} />
        </button>
      ))}
    </div>
  );
}
